import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { PageError, PageLoading, PageNotFound } from '@/components/ui/PageAsyncState'
import { useCan } from '@/features/auth/AuthContext'
import { productService, type Product } from '@/services/products'
import { variantService, type ProductVariant } from '@/services/variants'
import { bomService, type Bom } from '@/services/boms'
import {
  packagingBomService,
  type PackagingBom,
} from '@/services/packagingBoms'
import type { AssemblyType, IstaLevel } from '@/services/boms/types'
import { Typeahead } from '@/components/forms/Typeahead'
import {
  Save,
  CheckCircle2,
  AlertTriangle,
  ArrowLeft,
  Layers,
  Box,
  ExternalLink,
  FileSpreadsheet,
} from 'lucide-react'

export function ProductConditionMatrixPage() {
  const { productId } = useParams<{ productId: string }>()
  const navigate = useNavigate()
  const canWrite = useCan('bom.write')

  const [product, setProduct] = useState<Product | null>(null)
  const [variants, setVariants] = useState<ProductVariant[]>([])
  const [boms, setBoms] = useState<Bom[]>([])
  const [packagingBoms, setPackagingBoms] = useState<PackagingBom[]>([])

  // Maps state:
  // variantMaps: { [variantId_mode]: bomId | '' }
  const [variantMaps, setVariantMaps] = useState<Record<string, string>>({})
  // packMaps: { [mode_ista]: packagingBomFamilyId | '' }
  const [packMaps, setPackMaps] = useState<Record<string, string>>({})

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const loadData = useCallback(async () => {
    if (!productId) return
    setLoading(true)
    setError(null)
    setSuccessMessage(null)

    try {
      const [prod, vars, bomList, packList, packMapList] = await Promise.all([
        productService.getById(productId),
        variantService.listByProduct(productId),
        bomService.list({ product_id: productId, status: 'active' }),
        packagingBomService.list({ product_id: productId, status: 'active' }),
        packagingBomService.listMaps(productId),
      ])

      if (!prod) {
        setProduct(null)
        setError('Sản phẩm không tồn tại')
        return
      }

      setProduct(prod)
      setVariants(vars)
      setBoms(bomList)
      setPackagingBoms(packList)

      // Fetch variant bom maps for each variant and mode
      const vMaps: Record<string, string> = {}
      await Promise.all(
        vars.map(async (v) => {
          const [bomSx, bomKd] = await Promise.all([
            variantService.getBom(v.id, 'assembled').catch(() => null),
            variantService.getBom(v.id, 'kd').catch(() => null),
          ])
          if (bomSx) vMaps[`${v.id}_assembled`] = bomSx.id
          if (bomKd) vMaps[`${v.id}_kd`] = bomKd.id
        }),
      )
      setVariantMaps(vMaps)

      // Packaging maps
      const pMaps: Record<string, string> = {}
      for (const pm of packMapList) {
        pMaps[`${pm.assembly_type}_${pm.ista_level}`] = pm.packaging_bom_family_id
      }
      setPackMaps(pMaps)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không tải được dữ liệu ma trận')
    } finally {
      setLoading(false)
    }
  }, [productId])

  useEffect(() => {
    void loadData()
  }, [loadData])

  // Save changes
  const handleSave = async () => {
    if (!productId) return
    setSaving(true)
    setError(null)
    setSuccessMessage(null)

    try {
      // 1. Save Variant BOM maps
      for (const v of variants) {
        for (const mode of ['assembled', 'kd'] as AssemblyType[]) {
          const key = `${v.id}_${mode}`
          const selectedBomId = variantMaps[key]

          if (selectedBomId) {
            await variantService.putBom(v.id, selectedBomId, mode)
          } else {
            // Delete map if unmapped
            await variantService.deleteBom(v.id, mode).catch(() => {})
          }
        }
      }

      // 2. Save Packaging maps (4 combinations)
      const combos: Array<{ assembly_type: AssemblyType; ista_level: IstaLevel }> = [
        { assembly_type: 'assembled', ista_level: '1A' },
        { assembly_type: 'assembled', ista_level: '3A' },
        { assembly_type: 'kd', ista_level: '1A' },
        { assembly_type: 'kd', ista_level: '3A' },
      ]

      for (const c of combos) {
        const key = `${c.assembly_type}_${c.ista_level}`
        const familyId = packMaps[key]
        if (familyId) {
          await packagingBomService.upsertMap({
            product_id: productId,
            assembly_type: c.assembly_type,
            ista_level: c.ista_level,
            packaging_bom_family_id: familyId,
          })
        }
      }

      setSuccessMessage('Đã lưu ma trận điều kiện thành công!')
      void loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lưu ma trận thất bại')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <PageLoading label="Đang tải ma trận điều kiện BOM & Đóng gói..." />
  }

  if (error && !product) {
    return (
      <div className="p-6 space-y-4">
        <PageError message={error} onRetry={() => void loadData()} />
        <Button variant="secondary" onClick={() => navigate('/products')}>
          Về danh sách sản phẩm
        </Button>
      </div>
    )
  }

  if (!product) {
    return (
      <PageNotFound
        title="Không tìm thấy sản phẩm"
        description="Sản phẩm không tồn tại hoặc đã bị xóa."
        action={
          <Button variant="secondary" onClick={() => navigate('/products')}>
            Về danh sách sản phẩm
          </Button>
        }
      />
    )
  }

  // Filter BOMs by mode
  const assembledBoms = boms.filter((b) => b.assembly_type === 'assembled')
  const kdBoms = boms.filter((b) => b.assembly_type === 'kd')

  // Total possible BOM conditions = variants.length * 2
  const totalBomConditions = variants.length * 2
  const mappedBomCount = variants.reduce((acc, v) => {
    let count = 0
    if (variantMaps[`${v.id}_assembled`]) count++
    if (variantMaps[`${v.id}_kd`]) count++
    return acc + count
  }, 0)

  // Total packaging conditions = 4
  const packagingCombos: Array<{ assembly_type: AssemblyType; ista_level: IstaLevel }> = [
    { assembly_type: 'assembled', ista_level: '1A' },
    { assembly_type: 'assembled', ista_level: '3A' },
    { assembly_type: 'kd', ista_level: '1A' },
    { assembly_type: 'kd', ista_level: '3A' },
  ]
  const mappedPackCount = packagingCombos.filter(
    (c) => packMaps[`${c.assembly_type}_${c.ista_level}`],
  ).length

  return (
    <div className="space-y-6 pb-12">
      {/* 1. Breadcrumbs */}
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Sản phẩm', to: '/products' },
          { label: product.code, to: `/products/${product.id}` },
          { label: 'Ma trận điều kiện' },
        ]}
      />

      {/* 2. Page Header */}
      <div className="bg-white p-6 rounded-lg border border-slate-300 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Ma trận điều kiện:
            </span>
            <h1 className="text-2xl font-extrabold text-slate-900">{product.code}</h1>
            <span className="text-sm font-medium text-slate-600">({product.name})</span>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Kích thước: {`${product.w ?? '—'} × ${product.d ?? '—'} × ${product.h ?? '—'} mm`} ·{' '}
            {variants.length} màu/variant · Ghép nối BOM và Bộ đóng gói theo từng điều kiện sản xuất
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => navigate(`/products/${product.id}`)}
            className="flex items-center gap-1.5"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Chi tiết SP</span>
          </Button>

          <Button
            variant="secondary"
            onClick={() => navigate('/purchasing/boms/intake')}
            className="flex items-center gap-1.5"
          >
            <FileSpreadsheet className="h-4 w-4" />
            <span>Nhập từ Excel</span>
          </Button>

          {canWrite && (
            <Button
              variant="secondary"
              onClick={() => navigate(`/purchasing/boms/${product.id}/master`)}
              className="flex items-center gap-1.5"
            >
              <Layers className="h-4 w-4" />
              <span>Master BOM (Kỹ thuật)</span>
            </Button>
          )}

          {canWrite && (
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5"
            >
              <Save className="h-4 w-4" />
              <span>{saving ? 'Đang lưu...' : 'Lưu ma trận'}</span>
            </Button>
          )}
        </div>
      </div>

      {/* 3. Status alerts */}
      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 flex items-center gap-3 shadow-sm">
          <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-3 shadow-sm">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* KPI Overview banner */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white p-4 rounded-lg border border-slate-300 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-slate-100 text-slate-600">
              <Layers className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs text-slate-500 font-medium">BOM Sản phẩm đã map</div>
              <div className="text-lg font-bold text-slate-900">
                {mappedBomCount} / {totalBomConditions} điều kiện
              </div>
            </div>
          </div>
          {mappedBomCount === totalBomConditions && totalBomConditions > 0 ? (
            <Badge tone="success">Đầy đủ 100%</Badge>
          ) : (
            <Badge tone="warning">Thiếu {totalBomConditions - mappedBomCount} ô</Badge>
          )}
        </div>

        <div className="bg-white p-4 rounded-lg border border-slate-300 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-slate-100 text-slate-600">
              <Box className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs text-slate-500 font-medium">Bộ Đóng gói đã map</div>
              <div className="text-lg font-bold text-slate-900">{mappedPackCount} / 4 điều kiện</div>
            </div>
          </div>
          {mappedPackCount === 4 ? (
            <Badge tone="success">Đầy đủ 100%</Badge>
          ) : (
            <Badge tone="warning">Thiếu {4 - mappedPackCount} ô</Badge>
          )}
        </div>
      </div>

      {/* 4. PHẦN 1: MA TRẬN BOM (Màu × Mode) */}
      <section className="overflow-hidden rounded-lg border border-slate-300 bg-white">
        <div className="bg-slate-50 px-6 py-3 border-b border-slate-300 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h2 className="text-sm font-bold tracking-wider uppercase text-slate-900">
            1. Ma trận BOM sản phẩm (Variant / Màu × Chế độ lắp ráp)
          </h2>
          <span className="text-xs text-slate-500">
            Hàng = Màu sắc · Cột = Ráp chết (assembled) | Tháo rời (kd)
          </span>
        </div>

        {variants.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">
            Sản phẩm này chưa có Variant/Màu nào. Vui lòng tạo Variant ở trang Chi tiết sản phẩm.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse text-slate-900">
              <thead>
                <tr className="bg-slate-50 font-semibold text-slate-900">
                  <th className="py-2.5 px-3 w-12 text-center border border-slate-300">STT</th>
                  <th className="py-2.5 px-3 min-w-[200px] border border-slate-300">Màu sắc / Variant</th>
                  <th className="py-2.5 px-3 min-w-[280px] border border-slate-300">
                    Ráp chết (assembled / SX)
                  </th>
                  <th className="py-2.5 px-3 min-w-[280px] border border-slate-300">
                    Tháo rời (kd / KD)
                  </th>
                </tr>
              </thead>
              <tbody>
                {variants.map((v, idx) => {
                  const sxKey = `${v.id}_assembled`
                  const kdKey = `${v.id}_kd`
                  const sxBomId = variantMaps[sxKey] || ''
                  const kdBomId = variantMaps[kdKey] || ''

                  const isSxMapped = Boolean(sxBomId)
                  const isKdMapped = Boolean(kdBomId)

                  return (
                    <tr key={v.id} className="hover:bg-slate-50">
                      <td className="py-2 px-3 text-center font-mono text-slate-500 border border-slate-300 bg-white">
                        {idx + 1}
                      </td>
                      <td className="py-2 px-3 border border-slate-300 bg-white">
                        <div className="font-bold text-slate-900">{v.name || v.variantCode}</div>
                        <div className="text-xs font-mono text-slate-500">{v.variantCode}</div>
                      </td>

                      {/* Assembled Cell */}
                      <td
                        className={`py-2 px-3 border border-slate-300 ${
                          !isSxMapped ? 'bg-amber-50/70' : 'bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-full min-w-[200px]">
                            <Typeahead<Bom>
                              items={assembledBoms}
                              value={assembledBoms.find((b) => b.id === sxBomId) || null}
                              onChange={(item) =>
                                setVariantMaps((prev) => ({
                                  ...prev,
                                  [sxKey]: item ? item.id : '',
                                }))
                              }
                              displayValue={(b) => `${b.name} (v${b.version_seq})`}
                              placeholder="⚠ Chưa map BOM..."
                              disabled={!canWrite}
                              clearable
                              inputClassName={`text-xs py-1.5 px-2.5 font-medium transition ${
                                !isSxMapped
                                  ? 'border-amber-300 bg-amber-50 text-amber-800 focus:border-amber-500'
                                  : 'border-slate-300 bg-white text-slate-900 focus:border-slate-500'
                              }`}
                              renderItem={(b) => (
                                <div className="flex items-center justify-between py-1 text-xs">
                                  <span className="font-semibold text-slate-900 truncate mr-2">{b.name}</span>
                                  <span className="shrink-0 font-mono text-[11px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                    v{b.version_seq}
                                  </span>
                                </div>
                              )}
                            />
                          </div>

                          {isSxMapped && (
                            <Link
                              to={`/purchasing/boms/${sxBomId}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-slate-400 hover:text-slate-700 p-1 shrink-0"
                              title="Mở xem BOM"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Link>
                          )}
                        </div>
                      </td>

                      {/* KD Cell */}
                      <td
                        className={`py-2 px-3 border border-slate-300 ${
                          !isKdMapped ? 'bg-amber-50/70' : 'bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-full min-w-[200px]">
                            <Typeahead<Bom>
                              items={kdBoms}
                              value={kdBoms.find((b) => b.id === kdBomId) || null}
                              onChange={(item) =>
                                setVariantMaps((prev) => ({
                                  ...prev,
                                  [kdKey]: item ? item.id : '',
                                }))
                              }
                              displayValue={(b) => `${b.name} (v${b.version_seq})`}
                              placeholder="⚠ Chưa map BOM..."
                              disabled={!canWrite}
                              clearable
                              inputClassName={`text-xs py-1.5 px-2.5 font-medium transition ${
                                !isKdMapped
                                  ? 'border-amber-300 bg-amber-50 text-amber-800 focus:border-amber-500'
                                  : 'border-slate-300 bg-white text-slate-900 focus:border-slate-500'
                              }`}
                              renderItem={(b) => (
                                <div className="flex items-center justify-between py-1 text-xs">
                                  <span className="font-semibold text-slate-900 truncate mr-2">{b.name}</span>
                                  <span className="shrink-0 font-mono text-[11px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                    v{b.version_seq}
                                  </span>
                                </div>
                              )}
                            />
                          </div>

                          {isKdMapped && (
                            <Link
                              to={`/purchasing/boms/${kdBomId}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-slate-400 hover:text-slate-700 p-1 shrink-0"
                              title="Mở xem BOM"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* 5. PHẦN 2: MA TRẬN ĐÓNG GÓI (Mode × ISTA) */}
      <section className="overflow-hidden rounded-lg border border-slate-300 bg-white">
        <div className="bg-slate-50 px-6 py-3 border-b border-slate-300 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h2 className="text-sm font-bold tracking-wider uppercase text-slate-900">
            2. Ma trận Đóng gói (Chế độ lắp ráp × Tiêu chuẩn ISTA)
          </h2>
          <span className="text-xs text-slate-500">
            Dùng chung cho tất cả các màu của mã hàng {product.code}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse text-slate-900">
            <thead>
              <tr className="bg-slate-50 font-semibold text-slate-900">
                <th className="py-2.5 px-3 w-12 text-center border border-slate-300">STT</th>
                <th className="py-2.5 px-3 min-w-[180px] border border-slate-300">Chế độ lắp ráp</th>
                <th className="py-2.5 px-3 min-w-[140px] border border-slate-300">Tiêu chuẩn ISTA</th>
                <th className="py-2.5 px-3 min-w-[340px] border border-slate-300">
                  Bộ Đóng gói (Packaging BOM Family)
                </th>
              </tr>
            </thead>
            <tbody>
              {packagingCombos.map((c, idx) => {
                const key = `${c.assembly_type}_${c.ista_level}`
                const selectedFamilyId = packMaps[key] || ''
                const isMapped = Boolean(selectedFamilyId)

                return (
                  <tr key={key} className="hover:bg-slate-50">
                    <td className="py-2 px-3 text-center font-mono text-slate-500 border border-slate-300 bg-white">
                      {idx + 1}
                    </td>
                    <td className="py-2 px-3 font-semibold text-slate-900 border border-slate-300 bg-white">
                      {c.assembly_type === 'assembled' ? 'Ráp chết (assembled)' : 'Tháo rời (kd)'}
                    </td>
                    <td className="py-2 px-3 border border-slate-300 bg-white">
                      <Badge tone="neutral">{c.ista_level}</Badge>
                    </td>

                    {/* Packaging BOM Select Cell */}
                    <td
                      className={`py-2 px-3 border border-slate-300 ${
                        !isMapped ? 'bg-amber-50/70' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-full max-w-lg">
                          <Typeahead<PackagingBom>
                            items={packagingBoms}
                            value={
                              packagingBoms.find(
                                (pb) => pb.packaging_bom_family_id === selectedFamilyId,
                              ) || null
                            }
                            onChange={(item) =>
                              setPackMaps((prev) => ({
                                ...prev,
                                [key]: item ? item.packaging_bom_family_id : '',
                              }))
                            }
                            displayValue={(pb) => `${pb.name} (v${pb.version_seq})`}
                            placeholder="⚠ Chưa map Bộ đóng gói..."
                            disabled={!canWrite}
                            clearable
                            inputClassName={`text-xs py-1.5 px-2.5 font-medium transition ${
                              !isMapped
                                ? 'border-amber-300 bg-amber-50 text-amber-800 focus:border-amber-500'
                                : 'border-slate-300 bg-white text-slate-900 focus:border-slate-500'
                            }`}
                            renderItem={(pb) => (
                              <div className="flex items-center justify-between py-1 text-xs">
                                <span className="font-semibold text-slate-900 truncate mr-2">{pb.name}</span>
                                <span className="shrink-0 font-mono text-[11px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                  v{pb.version_seq}
                                </span>
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Save action button footer */}
      {canWrite && (
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-300">
          <Button
            variant="secondary"
            onClick={() => navigate(`/products/${product.id}`)}
          >
            Quay lại
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5"
          >
            <Save className="h-4 w-4" />
            <span>{saving ? 'Đang lưu...' : 'Lưu toàn bộ ma trận'}</span>
          </Button>
        </div>
      )}
    </div>
  )
}
